/* @flow */

import type { NETGEM_API_V8_METADATA_SERIES } from '../../../libs/netgemLibrary/v8/types/MetadataProgram';
import type { NETGEM_API_V8_URL_DEFINITION } from '../../../libs/netgemLibrary/v8/types/NtgVideoFeed';
import type { NETGEM_API_V8_SECTION } from '../../../libs/netgemLibrary/v8/types/Section';

import { getItemType } from '../../../libs/netgemLibrary/v8/helpers/Item';
import { ItemLocationKind, ItemType, type NETGEM_API_V8_FEED_ITEM } from '../../../libs/netgemLibrary/v8/types/FeedItem';
import {
  type NETGEM_API_V8_WIDGET_TILE_CONFIG_TYPE,
  type NetgemApiV8WidgetItemClick,
  NetgemApiV8WidgetItemClickActionKind,
  type TILE_CONFIG_DESCRIPTION,
  type TILE_CONFIG_TYPE,
  TileConfig,
  TileConfigTileType,
  TileContentImageKind,
  TileImageLayout,
  TileOnFocus,
} from '../../../libs/netgemLibrary/v8/types/WidgetConfig';
import { isUndefinedOrNull } from '../../jsHelpers/types';
import { getOnItemClick } from '../item/traverser';
import { type EXTENDED_ITEM, ExtendedItemType } from '../item/types';

const DefaultTileConfig: NETGEM_API_V8_WIDGET_TILE_CONFIG_TYPE = {
  caption: null,
  channelLogo: true,
  contentImage: TileContentImageKind.Program,
  hoverContent: null,
  imageLayout: TileImageLayout.Stretched,
  onFocus: TileOnFocus.Details,
  type: TileConfigTileType.Gemtv,
};

// Temporary function until tileConfig is used everywhere and widgetConfig.tileType completely deprecated
const completeIfNeeded: (tileConfig: NETGEM_API_V8_WIDGET_TILE_CONFIG_TYPE, sectionId: string) => TILE_CONFIG_TYPE = (tileConfig, sectionId) => {
  const { caption, channelLogo, contentImage, contentImageVariant, hoverContent, imageLayout, onFocus, type } = tileConfig;
  const completeTileConfig: TILE_CONFIG_TYPE = {
    caption,
    channelLogo,
    contentImage,
    contentImageVariant,
    hoverContent,
    imageLayout,
    onFocus,
    type: type ?? TileConfigTileType.Gemtv,
  };

  if (!contentImage) {
    completeTileConfig.contentImage = TileContentImageKind.Program;
  }

  if (!imageLayout) {
    completeTileConfig.imageLayout = TileImageLayout.Stretched;
  }

  if (!onFocus) {
    completeTileConfig.onFocus = TileOnFocus.Details;
  }

  if (!type) {
    completeTileConfig.type = TileConfigTileType.Gemtv;
  }

  if ((type === TileConfigTileType.Portrait || type === TileConfigTileType.LandscapeVod) && isUndefinedOrNull(hoverContent)) {
    completeTileConfig.channelLogo = false;
    completeTileConfig.contentImage = TileContentImageKind.Series;
    completeTileConfig.hoverContent = {
      program: [TileConfig.ProgramTitle, TileConfig.ProgramSynopsis, TileConfig.Duration, TileConfig.Year, TileConfig.Genre, TileConfig.Language, TileConfig.ParentalGuidance],
      series: [TileConfig.SeriesTitle, TileConfig.SeriesSynopsis, TileConfig.Duration, TileConfig.Year, TileConfig.Genre, TileConfig.ParentalGuidance],
    };
  }
  if (completeTileConfig.type === TileConfigTileType.Gemtv) {
    completeTileConfig.contentImage = TileContentImageKind.Series;

    if (isUndefinedOrNull(hoverContent)) {
      completeTileConfig.hoverContent = {
        program: [TileConfig.SeriesTitle, TileConfig.ProgramTitle, TileConfig.ProgramSynopsis, TileConfig.Duration, TileConfig.Year],
        series: [TileConfig.SeriesTitle, TileConfig.ProgramTitle, TileConfig.ProgramSynopsis, TileConfig.Duration, TileConfig.Year],
      };
    }
    if (isUndefinedOrNull(caption)) {
      completeTileConfig.caption = {
        program: [TileConfig.SeriesTitle, TileConfig.ProgramTitle, TileConfig.Duration, TileConfig.Year],
        series: [TileConfig.SeriesTitle, TileConfig.ProgramTitle, TileConfig.Duration, TileConfig.Year],
      };
    }
  }
  if (type === TileConfigTileType.Landscape && isUndefinedOrNull(hoverContent)) {
    completeTileConfig.contentImage = TileContentImageKind.Series;
    completeTileConfig.hoverContent = {
      program: [TileConfig.ProgramTitle, TileConfig.ProgramSynopsis, TileConfig.Duration, TileConfig.Year, TileConfig.Genre, TileConfig.Language, TileConfig.ParentalGuidance],
      series: [TileConfig.SeriesTitle, TileConfig.SeriesSynopsis, TileConfig.Duration, TileConfig.Year, TileConfig.Genre, TileConfig.ParentalGuidance],
    };
  }

  if (sectionId === 'jeunesse_vod_universe_section_id' || sectionId === 'universcine_vod_thematic_universe_section_id') {
    completeTileConfig.onFocus = TileOnFocus.Selection;
    completeTileConfig.contentImage = TileContentImageKind.Other;
  }

  if (type) {
    if ((type as string).endsWith('.fill')) {
      completeTileConfig.imageLayout = TileImageLayout.Cover;
    }

    if (
      [
        TileConfigTileType.Channel,
        TileConfigTileType.ChannelGroup,
        TileConfigTileType.ChannelGroupBig,
        TileConfigTileType.ChannelGroupFill,
        TileConfigTileType.ChannelGroupBigFill,
        TileConfigTileType.Deeplink3x1,
        TileConfigTileType.Deeplink5x1,
      ].includes(type)
    ) {
      completeTileConfig.onFocus = TileOnFocus.Selection;
    }
  }

  return completeTileConfig;
};

const getTileConfig: (section: NETGEM_API_V8_SECTION) => TILE_CONFIG_TYPE = (section) => {
  const { id, widgetConfig } = section;

  if (!widgetConfig?.tileConfig) {
    return completeIfNeeded(DefaultTileConfig, id);
  }

  const { tileConfig } = widgetConfig;
  return completeIfNeeded(tileConfig, id);
};

const getTileTypeClass: (type: ?TileConfigTileType) => string = (type) =>
  type
    ? (type as string)
        .replace(/deeplink\.(\d+x\d+)/gu, 'deeplink$1')
        .replace(/\./gu, ' ')
        .replace('fill', 'cover')
    : '';

const getTileCaption: (tileConfig: TILE_CONFIG_TYPE, itemType: ?ItemType) => Set<TileConfig> = (tileConfig, itemType) => {
  const { caption } = tileConfig;
  return getTileTextDescription(caption, itemType);
};

const getTileHoverContent: (tileConfig: TILE_CONFIG_TYPE, itemType: ?ItemType) => Set<TileConfig> = (tileConfig, itemType) => {
  const { hoverContent } = tileConfig;
  return getTileTextDescription(hoverContent, itemType);
};

const getTileTextDescription: (textDescription: ?TILE_CONFIG_DESCRIPTION, itemType: ?ItemType) => Set<TileConfig> = (textDescription, itemType) => {
  if (!textDescription || !itemType) {
    // No description
    return new Set<TileConfig>();
  }

  let descriptionByType: ?Array<TileConfig> = null;

  if (itemType === ItemType.Series) {
    ({ series: descriptionByType } = textDescription);
  } else {
    // ItemType.Program and everything else
    ({ program: descriptionByType } = textDescription);
  }

  if (!descriptionByType) {
    // No description for this item type
    return new Set<TileConfig>();
  }

  return new Set(descriptionByType);
};

const getCardUrlDefinitionForItem: (
  item: NETGEM_API_V8_FEED_ITEM,
  seriesMetadata: NETGEM_API_V8_METADATA_SERIES | null,
  onItemClick?: NetgemApiV8WidgetItemClick,
  defaultOnItemClick: ?NetgemApiV8WidgetItemClick,
) => NETGEM_API_V8_URL_DEFINITION | null = (item, seriesMetadata, onItemClick, defaultOnItemClick) => {
  const { id, locType } = item;
  const itemType = getItemType(id);

  // Null check is superfluous here but Flow complains, so...
  if (itemType === null || (itemType !== ItemType.Program && itemType !== ItemType.Series)) {
    return null;
  }

  const localOnItemClick = onItemClick ?? defaultOnItemClick;
  if (!localOnItemClick || !Array.isArray(localOnItemClick.params)) {
    return null;
  }

  const fullItem: EXTENDED_ITEM = {
    item,
    seriesMetadata,
    type: locType === ItemLocationKind.Recording || locType === ItemLocationKind.Catchup || locType === ItemLocationKind.ScheduledEvent ? ExtendedItemType.TV : ExtendedItemType.VOD,
  };

  const matchingOnItemClick = getOnItemClick(fullItem, localOnItemClick);

  if (matchingOnItemClick === null) {
    // No action found
    return null;
  }

  const { action, params } = matchingOnItemClick;

  if (action !== NetgemApiV8WidgetItemClickActionKind.Card) {
    return null;
  }

  if (params && typeof params === 'object' && 'uri' in params) {
    return params as Object as NETGEM_API_V8_URL_DEFINITION;
  }

  return null;
};

export { getCardUrlDefinitionForItem, getTileCaption, getTileConfig, getTileHoverContent, getTileTypeClass };
